import { collection, addDoc, getDocs, doc, deleteDoc, updateDoc, onSnapshot, query, orderBy, writeBatch, getDoc, setDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../config/firebaseConfig';

// Firebase initialization is now centralized in src/config/firebaseConfig.js
// This prevents "Firebase App named '[DEFAULT]' already exists" errors

console.log('🔥 Firebase Service connected to Firestore');

class FirebaseService {
  constructor() {
    this.db = db;
    this.ordersCollection = 'orders';
  }

  // Save order to Firebase
  async saveOrder(orderData) {
    try {
      console.log('💾 Saving order to Firebase:', orderData);

      const docRef = await addDoc(collection(this.db, this.ordersCollection), {
        ...orderData,
        createdAt: new Date(),
        updatedAt: new Date()
      });

      console.log('✅ Order saved to Firebase with ID:', docRef.id);

      return {
        success: true,
        id: docRef.id,
        data: orderData
      };
    } catch (error) {
      console.error('❌ Error saving order to Firebase:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  // Add order to Firebase (alias for saveOrder)
  async addOrder(orderData) {
    return await this.saveOrder(orderData);
  }

  // Get all orders from Firebase
  async getOrders() {
    try {
      console.log('📥 Loading orders from Firebase...');

      const ordersRef = collection(this.db, this.ordersCollection);
      const q = query(ordersRef, orderBy('createdAt', 'desc'));
      const querySnapshot = await getDocs(q);

      const orders = [];
      querySnapshot.forEach((doc) => {
        orders.push({
          id: doc.id,
          ...doc.data()
        });
      });

      console.log(`✅ Orders loaded from Firebase: ${orders.length} orders`);
      return {
        success: true,
        orders: orders
      };
    } catch (error) {
      console.error('❌ Error loading orders from Firebase:', error);
      return {
        success: false,
        error: error.message,
        orders: []
      };
    }
  }

  // Delete order from Firebase
  async deleteOrder(orderId) {
    try {
      console.log('🗑️ Deleting order from Firebase:', orderId);
      console.log('📊 Database:', this.db);
      console.log('📊 Collection:', this.ordersCollection);

      const orderRef = doc(this.db, this.ordersCollection, orderId);
      console.log('📊 Order reference:', orderRef);

      await deleteDoc(orderRef);

      console.log(`✅ Order ${orderId} deleted from Firebase successfully`);

      return {
        success: true,
        id: orderId
      };
    } catch (error) {
      console.error('❌ Error deleting order from Firebase:', error);
      console.error('❌ Error details:', {
        code: error.code,
        message: error.message,
        stack: error.stack
      });
      return {
        success: false,
        error: error.message
      };
    }
  }

  // Update order status in Firebase
  async updateOrderStatus(orderId, newStatus) {
    try {
      console.log(`🔄 Updating order ${orderId} status to: ${newStatus}`);

      // Try to find order by ID first
      const orderRef = doc(this.db, this.ordersCollection, orderId);

      try {
        const orderSnap = await getDoc(orderRef);

        if (orderSnap.exists()) {
          // Order found by direct ID, update it
          await updateDoc(orderRef, {
            status: newStatus,
            updatedAt: new Date()
          });

          console.log(`✅ Order ${orderId} status updated to ${newStatus}`);

          return {
            success: true,
            id: orderId,
            status: newStatus
          };
        } else {
          console.log(`⚠️ Order ${orderId} not found by direct ID, trying to find by field...`);
        }
      } catch (docError) {
        console.log(`⚠️ Error accessing order by ID: ${docError.message}`);
      }

      // If not found by direct ID, try to find by searching all orders
      console.log('🔍 Searching for order in all documents...');
      const ordersSnapshot = await getDocs(collection(this.db, this.ordersCollection));

      let foundOrder = null;
      let foundDocId = null;

      ordersSnapshot.forEach((doc) => {
        const data = doc.data();
        if (data.id === orderId || doc.id === orderId) {
          foundOrder = data;
          foundDocId = doc.id;
        }
      });

      if (foundOrder && foundDocId) {
        // Update the found order
        const foundOrderRef = doc(this.db, this.ordersCollection, foundDocId);
        await updateDoc(foundOrderRef, {
          status: newStatus,
          updatedAt: new Date()
        });

        console.log(`✅ Order found and updated: ${foundDocId} -> ${newStatus}`);

        return {
          success: true,
          id: foundDocId,
          status: newStatus
        };
      } else {
        console.error(`❌ Order ${orderId} not found in any document`);
        return {
          success: false,
          error: 'Order not found in Firebase database'
        };
      }

    } catch (error) {
      console.error('❌ Error updating order status:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  // Listen to real-time updates
  subscribeToOrders(callback) {
    try {
      console.log('👂 Setting up real-time listener for orders...');

      const ordersRef = collection(this.db, this.ordersCollection);
      const q = query(ordersRef, orderBy('createdAt', 'desc'));

      const unsubscribe = onSnapshot(q, (querySnapshot) => {
        const orders = [];
        querySnapshot.forEach((doc) => {
          orders.push({
            id: doc.id,
            ...doc.data()
          });
        });

        console.log(`🔥 Firebase orders updated: ${orders.length} orders`);
        callback({
          success: true,
          orders: orders
        });
      }, (error) => {
        console.error('❌ Error in real-time listener:', error);
        callback({
          success: false,
          error: error.message,
          orders: []
        });
      });

      return unsubscribe;
    } catch (error) {
      console.error('❌ Error setting up real-time listener:', error);
      return null;
    }
  }

  // Test Firebase connection
  async testConnection() {
    try {
      console.log('🧪 Testing Firebase connection...');

      const testRef = collection(this.db, 'test');
      await addDoc(testRef, {
        message: 'Firebase connection test',
        timestamp: new Date()
      });

      console.log('✅ Firebase connection test successful');
      return {
        success: true,
        message: 'Firebase connection is working'
      };
    } catch (error) {
      console.error('❌ Firebase connection test failed:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  // Test delete functionality
  async testDelete() {
    try {
      console.log('🧪 Testing delete functionality...');

      // First, get all orders to see what we have
      const ordersResult = await this.getOrders();
      console.log('📊 Current orders:', ordersResult.orders.length);

      if (ordersResult.orders.length > 0) {
        const firstOrder = ordersResult.orders[0];
        console.log('📊 First order ID:', firstOrder.id);

        // Try to delete the first order
        const deleteResult = await this.deleteOrder(firstOrder.id);
        console.log('📊 Delete result:', deleteResult);

        return deleteResult;
      } else {
        console.log('📊 No orders to test delete with');
        return {
          success: true,
          message: 'No orders to test delete with'
        };
      }
    } catch (error) {
      console.error('❌ Test delete failed:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  // Delete all orders (Secret WallZ function) - Moves to bin for sync
  async deleteAllOrders() {
    try {
      console.log('🗑️ Delete All Orders: Moving all orders to bin...');

      const ordersResult = await this.getOrders();
      if (!ordersResult.success || ordersResult.orders.length === 0) {
        return { success: true, message: 'No orders found', deletedCount: 0 };
      }

      const orders = ordersResult.orders;
      const batch = writeBatch(this.db);

      for (const order of orders) {
        // 1. Add to deleted_orders
        const deletedRef = doc(this.db, 'deleted_orders', order.id);
        batch.set(deletedRef, {
          ...order,
          deletedAt: new Date().toISOString(),
          originalId: order.id
        });

        // 2. Delete from orders
        const orderRef = doc(this.db, this.ordersCollection, order.id);
        batch.delete(orderRef);
      }

      await batch.commit();
      console.log(`✅ Bulk soft-delete successful: ${orders.length} orders moved to bin`);

      return {
        success: true,
        message: `Successfully moved ${orders.length} orders to bin`,
        deletedCount: orders.length
      };
    } catch (error) {
      console.error('❌ Error in deleteAllOrders:', error);
      return this._handleError(error, 'deleteAllOrders');
    }
  }

  // Alternative delete method - Force delete all orders
  async forceDeleteAllOrders() {
    try {
      console.log('🗑️ Force Delete: Starting force deletion...');

      // Get all orders
      const ordersResult = await this.getOrders();
      if (!ordersResult.success || ordersResult.orders.length === 0) {
        return {
          success: true,
          message: 'No orders found to delete',
          deletedCount: 0
        };
      }

      const orders = ordersResult.orders;
      console.log(`📊 Force Delete: Found ${orders.length} orders to delete`);

      // Try individual deletion with retry mechanism
      let deletedCount = 0;
      let failedCount = 0;

      for (const order of orders) {
        try {
          const orderRef = doc(this.db, this.ordersCollection, order.id);
          await deleteDoc(orderRef);
          deletedCount++;
          console.log(`✅ Force Delete: Order ${order.id} deleted`);
        } catch (error) {
          failedCount++;
          console.error(`❌ Force Delete: Failed to delete order ${order.id}:`, error);
        }
      }

      console.log(`✅ Force Delete: Deleted ${deletedCount} orders, failed ${failedCount}`);

      return {
        success: deletedCount > 0,
        message: `Force deleted ${deletedCount} orders, failed ${failedCount}`,
        deletedCount: deletedCount,
        failedCount: failedCount
      };
    } catch (error) {
      console.error('❌ Force Delete: Error in force deletion:', error);
      return {
        success: false,
        error: error.message,
        deletedCount: 0
      };
    }
  }

  // --- DELETED ORDERS MANAGEMENT (Using 'deleted_orders' Collection) ---

  // Helper to handle permission errors
  _handleError(error, context) {
    if (error.code === 'permission-denied' || (error.message && error.message.includes('insufficient permissions'))) {
      console.error(`❌ Permission denied in ${context}. The collections need valid Security Rules.`);
      return {
        success: false,
        error: `تحتاج إلى تحديث قواعد الأمان (Rules) في Firebase للسماح بالوصول إلى مجموعات البيانات الجديدة (promo_codes / deleted_orders). تأكد من الضغط على زر Publish بعد التعديل.`
      };
    }
    console.error(`❌ Error in ${context}:`, error);
    return { success: false, error: error.message };
  }

  // Soft delete order (Move to Bin)
  async softDeleteOrder(order) {
    try {
      console.log('🗑️ Moving order to bin (soft delete):', order.id);

      // 1. Add to 'deleted_orders' collection
      const deletedOrderData = {
        ...order,
        deletedAt: new Date().toISOString(),
        originalId: order.id // Keep reference
      };

      // Use original ID for the document in deleted_orders to avoid duplicates
      await setDoc(doc(this.db, 'deleted_orders', order.id), deletedOrderData);

      // 2. Delete from 'orders' collection
      await deleteDoc(doc(this.db, this.ordersCollection, order.id));

      console.log('✅ Order moved to deleted_orders collection successfully');
      return { success: true };
    } catch (error) {
      return this._handleError(error, 'softDeleteOrder');
    }
  }

  // Permanently delete order (From Bin)
  async permanentDeleteOrder(orderId) {
    try {
      console.log('🔥 Permanently deleting order:', orderId);

      // Delete from 'deleted_orders' collection
      await deleteDoc(doc(this.db, 'deleted_orders', orderId));

      // Also try deleting from main orders collection just in case it's still there
      await deleteDoc(doc(this.db, this.ordersCollection, orderId));

      console.log('✅ Order permanently deleted from database');
      return { success: true };
    } catch (error) {
      return this._handleError(error, 'permanentDeleteOrder');
    }
  }

  // Restore order (Move from Bin to Active)
  async restoreOrder(order) {
    try {
      console.log('♻️ Restoring order:', order.id);

      // 1. Add back to 'orders' collection
      const orderData = { ...order };
      delete orderData.deletedAt; // Remove deletion metadata
      delete orderData.originalId;

      await setDoc(doc(this.db, this.ordersCollection, order.id), orderData);

      // 2. Remove from 'deleted_orders' collection
      await deleteDoc(doc(this.db, 'deleted_orders', order.id));

      console.log('✅ Order restored to active orders successfully');
      return { success: true };
    } catch (error) {
      return this._handleError(error, 'restoreOrder');
    }
  }

  // Get deleted orders (from collection)
  async getDeletedOrders() {
    try {
      console.log('📋 Loading deleted orders from collection...');
      const querySnapshot = await getDocs(collection(this.db, 'deleted_orders'));
      const deletedOrders = [];

      querySnapshot.forEach((doc) => {
        deletedOrders.push({ id: doc.id, ...doc.data() });
      });

      console.log(`✅ Loaded ${deletedOrders.length} deleted orders`);
      return { success: true, deletedOrders };
    } catch (error) {
      return this._handleError(error, 'getDeletedOrders');
    }
  }

  // Subscribe to real-time deleted orders
  subscribeToDeletedOrders(callback) {
    try {
      console.log('👂 Setting up real-time listener for DELETED orders...');
      const deletedRef = collection(this.db, 'deleted_orders');

      const unsubscribe = onSnapshot(deletedRef, (querySnapshot) => {
        const deletedOrders = [];
        querySnapshot.forEach((doc) => {
          deletedOrders.push({ id: doc.id, ...doc.data() });
        });

        console.log(`🔥 Firebase DELETED orders synced: ${deletedOrders.length}`);
        callback({ success: true, deletedOrders });
      }, (error) => {
        callback({ success: false, error: error.message });
      });

      return unsubscribe;
    } catch (e) {
      console.error(e);
      return null;
    }
  }

  // Legacy method purely for compatibility (can be removed later)
  async saveDeletedOrders(deletedOrders) {
    console.warn('⚠️ saveDeletedOrders is deprecated. Use softDeleteOrder instead.');
    return { success: true };
  }

  // --- PROMO CODE MANAGEMENT ---

  // Create a new promo code
  async createPromoCode(promoData) {
    try {
      console.log('🎫 Creating promo code:', promoData.code);

      // Check if code already exists
      const existing = await this.getPromoCodeByCode(promoData.code);
      if (existing.success && existing.promo) {
        return { success: false, error: 'كود الخصم موجود بالفعل' };
      }

      const promoDoc = {
        code: promoData.code.toUpperCase().trim(),
        discountType: promoData.discountType, // 'percentage' or 'fixed'
        discountValue: Number(promoData.discountValue),
        maxUses: promoData.maxUses ? Number(promoData.maxUses) : null, // null = unlimited
        currentUses: 0,
        minOrderAmount: promoData.minOrderAmount ? Number(promoData.minOrderAmount) : 0,
        expiryDate: promoData.expiryDate || null, // null = no expiry
        isActive: true,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      const docRef = await addDoc(collection(this.db, 'promo_codes'), promoDoc);
      console.log('✅ Promo code created:', docRef.id);
      return { success: true, id: docRef.id, promo: promoDoc };
    } catch (error) {
      return this._handleError(error, 'createPromoCode');
    }
  }

  // Get all promo codes
  async getPromoCodes() {
    try {
      const querySnapshot = await getDocs(collection(this.db, 'promo_codes'));
      const promoCodes = [];
      querySnapshot.forEach((doc) => {
        promoCodes.push({ id: doc.id, ...doc.data() });
      });
      console.log(`✅ Loaded ${promoCodes.length} promo codes`);
      return { success: true, promoCodes };
    } catch (error) {
      return this._handleError(error, 'getPromoCodes');
    }
  }

  // Get promo code by code string
  async getPromoCodeByCode(code) {
    try {
      const querySnapshot = await getDocs(collection(this.db, 'promo_codes'));
      let found = null;
      querySnapshot.forEach((docSnap) => {
        const data = docSnap.data();
        if (data.code === code.toUpperCase().trim()) {
          found = { id: docSnap.id, ...data };
        }
      });
      return { success: true, promo: found };
    } catch (error) {
      return this._handleError(error, 'getPromoCodeByCode');
    }
  }

  // Delete a promo code
  async deletePromoCode(promoId) {
    try {
      console.log('🗑️ Deleting promo code:', promoId);
      await deleteDoc(doc(this.db, 'promo_codes', promoId));
      console.log('✅ Promo code deleted');
      return { success: true };
    } catch (error) {
      return this._handleError(error, 'deletePromoCode');
    }
  }

  // Toggle promo code active/inactive
  async togglePromoCode(promoId, isActive) {
    try {
      await updateDoc(doc(this.db, 'promo_codes', promoId), {
        isActive: isActive,
        updatedAt: new Date().toISOString()
      });
      console.log(`✅ Promo code ${isActive ? 'activated' : 'deactivated'}`);
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  // Validate a promo code (for customer use)
  async validatePromoCode(code, orderSubtotal) {
    try {
      const result = await this.getPromoCodeByCode(code);
      if (!result.success || !result.promo) {
        return { success: false, error: 'كود الخصم غير صحيح' };
      }

      const promo = result.promo;

      // Check if active
      if (!promo.isActive) {
        return { success: false, error: 'كود الخصم غير نشط' };
      }

      // Check expiry
      if (promo.expiryDate && new Date(promo.expiryDate) < new Date()) {
        return { success: false, error: 'كود الخصم منتهي الصلاحية' };
      }

      // Check usage limit
      if (promo.maxUses !== null && promo.currentUses >= promo.maxUses) {
        return { success: false, error: 'كود الخصم تم استخدامه الحد الأقصى من المرات' };
      }

      // Check minimum order amount
      if (promo.minOrderAmount && orderSubtotal < promo.minOrderAmount) {
        return { success: false, error: `الحد الأدنى للطلب هو ${promo.minOrderAmount} EGP` };
      }

      // Calculate discount
      let discount = 0;
      if (promo.discountType === 'percentage') {
        discount = (orderSubtotal * promo.discountValue) / 100;
      } else {
        discount = promo.discountValue;
      }

      // Discount should not exceed order subtotal
      discount = Math.min(discount, orderSubtotal);

      return {
        success: true,
        promo: promo,
        discount: Math.round(discount * 100) / 100,
        discountLabel: promo.discountType === 'percentage'
          ? `${promo.discountValue}%`
          : `${promo.discountValue} EGP`
      };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  // --- ADMIN LOGGING ---

  // Helper to get readable device info
  _getDeviceInfo() {
    const ua = navigator.userAgent;

    let deviceType = "Desktop/Laptop";
    if (/Mobi|Android|iPhone|iPad/i.test(ua)) {
      deviceType = "Mobile/Tablet";
    }

    let os = "Unknown OS";
    if (ua.indexOf("Win") !== -1) os = "Windows";
    if (ua.indexOf("Mac") !== -1) os = "MacOS";
    if (ua.indexOf("Linux") !== -1) os = "Linux";
    if (ua.indexOf("Android") !== -1) os = "Android";
    if (ua.indexOf("like Mac") !== -1) os = "iOS";

    return `${deviceType} (${os})`;
  }

  // Log admin login event with location and readable device info
  async logAdminLogin(username) {
    try {
      console.log('📝 Logging admin access for:', username);

      let locationInfo = "Unknown Location";
      try {
        // Fetch location info from IP (using a free service)
        const response = await fetch('https://ipapi.co/json/');
        const data = await response.json();
        if (data && data.city) {
          locationInfo = `${data.city}, ${data.country_name}`;
        }
      } catch (locError) {
        console.warn('Could not fetch location:', locError);
      }

      const logData = {
        username: username,
        loginTime: new Date().toISOString(),
        timestamp: new Date(),
        device: this._getDeviceInfo(),
        location: locationInfo,
        userAgent: navigator.userAgent, // Keep for debugging if needed
      };

      await addDoc(collection(this.db, 'admin_logs'), logData);
      console.log('✅ Admin login logged with enhanced info');
      return { success: true };
    } catch (error) {
      console.error('❌ Error logging admin login:', error);
      return { success: false, error: error.message };
    }
  }

  // --- MAINTENANCE MODE MANAGEMENT ---

  // Get maintenance mode status
  async getMaintenanceMode() {
    try {
      const docRef = doc(this.db, 'settings', 'maintenance');
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        return { success: true, isEnabled: docSnap.data().isEnabled === true };
      }
      return { success: true, isEnabled: false };
    } catch (error) {
      console.error('❌ Error getting maintenance mode:', error);
      return { success: false, isEnabled: false };
    }
  }

  // Set maintenance mode status
  async setMaintenanceMode(isEnabled) {
    try {
      const docRef = doc(this.db, 'settings', 'maintenance');
      await setDoc(docRef, {
        isEnabled,
        updatedAt: new Date().toISOString()
      }, { merge: true });
      console.log(`✅ Maintenance mode ${isEnabled ? 'ENABLED' : 'DISABLED'}`);
      return { success: true };
    } catch (error) {
      console.error('❌ Error setting maintenance mode:', error);
      return { success: false, error: error.message };
    }
  }

  // Subscribe to real-time maintenance mode changes
  subscribeToMaintenanceMode(callback) {
    try {
      const docRef = doc(this.db, 'settings', 'maintenance');
      const unsubscribe = onSnapshot(docRef, (docSnap) => {
        const isEnabled = docSnap.exists() ? docSnap.data().isEnabled === true : false;
        callback({ success: true, isEnabled });
      }, (error) => {
        console.error('❌ Maintenance mode listener error:', error);
        callback({ success: false, isEnabled: false });
      });
      return unsubscribe;
    } catch (error) {
      console.error('❌ Error subscribing to maintenance mode:', error);
      return null;
    }
  }

  // Use promo code (increment usage count)
  async usePromoCode(promoId) {
    try {
      const promoRef = doc(this.db, 'promo_codes', promoId);
      const promoSnap = await getDoc(promoRef);

      if (promoSnap.exists()) {
        const currentUses = promoSnap.data().currentUses || 0;
        await updateDoc(promoRef, {
          currentUses: currentUses + 1,
          updatedAt: new Date().toISOString()
        });
        console.log('✅ Promo code usage incremented');
      }
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  // --- CMS MANAGEMENT (Products, Categories, Settings) ---
  
  async getProducts() {
    try {
      const querySnapshot = await getDocs(collection(this.db, 'products'));
      const products = [];
      querySnapshot.forEach((doc) => {
        products.push({ _dbId: doc.id, ...doc.data() });
      });
      return { success: true, products };
    } catch (error) {
      return this._handleError(error, 'getProducts');
    }
  }

  async saveProduct(product) {
    try {
      if (product._dbId) {
        const productRef = doc(this.db, 'products', product._dbId);
        const dataToSave = { ...product };
        delete dataToSave._dbId;
        await updateDoc(productRef, { ...dataToSave, updatedAt: new Date().toISOString() });
        return { success: true, id: product._dbId };
      } else {
        const docRef = await addDoc(collection(this.db, 'products'), {
          ...product,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        });
        return { success: true, id: docRef.id };
      }
    } catch (error) {
      return this._handleError(error, 'saveProduct');
    }
  }

  async deleteProduct(dbId) {
    try {
      await deleteDoc(doc(this.db, 'products', dbId));
      return { success: true };
    } catch (error) {
      return this._handleError(error, 'deleteProduct');
    }
  }

  async getCategories() {
    try {
      const querySnapshot = await getDocs(collection(this.db, 'categories'));
      const categories = [];
      querySnapshot.forEach((doc) => {
        categories.push({ _dbId: doc.id, ...doc.data() });
      });
      return { success: true, categories };
    } catch (error) {
      return this._handleError(error, 'getCategories');
    }
  }

  async saveCategory(category) {
    try {
      if (category._dbId) {
        const categoryRef = doc(this.db, 'categories', category._dbId);
        const dataToSave = { ...category };
        delete dataToSave._dbId;
        await updateDoc(categoryRef, { ...dataToSave, updatedAt: new Date().toISOString() });
        return { success: true, id: category._dbId };
      } else {
        const docRef = await addDoc(collection(this.db, 'categories'), {
          ...category,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        });
        return { success: true, id: docRef.id };
      }
    } catch (error) {
      return this._handleError(error, 'saveCategory');
    }
  }

  async deleteCategory(dbId) {
    try {
      await deleteDoc(doc(this.db, 'categories', dbId));
      return { success: true };
    } catch (error) {
      return this._handleError(error, 'deleteCategory');
    }
  }

  async getSiteSettings() {
    try {
      const docRef = doc(this.db, 'settings', 'site');
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        return { success: true, settings: docSnap.data() };
      }
      return { success: true, settings: {} };
    } catch (error) {
      return this._handleError(error, 'getSiteSettings');
    }
  }

  async updateSiteSettings(settings) {
    try {
      const docRef = doc(this.db, 'settings', 'site');
      await setDoc(docRef, {
        ...settings,
        updatedAt: new Date().toISOString()
      }, { merge: true });
      return { success: true };
    } catch (error) {
      return this._handleError(error, 'updateSiteSettings');
    }
  }

  subscribeToSiteContent(callback) {
    try {
      let content = { products: [], categories: [], settings: {} };
      
      const unsubscribeProducts = onSnapshot(collection(this.db, 'products'), (snap) => {
        content.products = snap.docs.map(doc => ({ _dbId: doc.id, ...doc.data() }));
        callback({ success: true, ...content });
      });
      
      const unsubscribeCategories = onSnapshot(collection(this.db, 'categories'), (snap) => {
        content.categories = snap.docs.map(doc => ({ _dbId: doc.id, ...doc.data() }));
        callback({ success: true, ...content });
      });

      const unsubscribeSettings = onSnapshot(doc(this.db, 'settings', 'site'), (docSnap) => {
        if(docSnap.exists()) {
           content.settings = docSnap.data();
           callback({ success: true, ...content });
        }
      });
      
      return () => {
        unsubscribeProducts();
        unsubscribeCategories();
        unsubscribeSettings();
      };
    } catch(error) {
      return null;
    }
  }

  async uploadImage(file, path) {
    try {
      const storageRef = ref(storage, `${path}/${Date.now()}_${file.name}`);
      const snapshot = await uploadBytes(storageRef, file);
      const url = await getDownloadURL(snapshot.ref);
      return { success: true, url };
    } catch (error) {
      return this._handleError(error, 'uploadImage');
    }
  }
}

// Create and export a singleton instance
const firebaseService = new FirebaseService();
export default firebaseService;